'use client';

import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { ViewPortHeight } from 'ui-components/layout/styles';
import { useViewPort } from 'ui-hooks/useViewPort';
import { ZIndex } from 'constants/zIndex';
import { configure } from 'utils/configure';

interface LoadingProps {
  className?: string;
}

const TransitionTime = 400;

export const Loading = ({ className }: LoadingProps) => (
  <Container className={className}>
    <StyledLoadingImage alt="loading" src={`${configure.staticPrefix}/images/loading.gif?23032703`} />
  </Container>
);

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const FullPageLoading = ({
  relative = false,
  backgroundColor = 'transparent',
}: {
  relative?: boolean;
  backgroundColor?: string;
}) => {
  const { height: viewportHeight } = useViewPort();
  return <StyledFullPageLoading height={viewportHeight} relative={relative} backgroundColor={backgroundColor} />;
};

export const TransitionLoading = ({
  backgroundColor = 'transparent',
  isShow,
}: {
  backgroundColor?: string;
  isShow: boolean;
}) => {
  const [show, setShow] = useState(isShow);
  const [attach, setAttach] = useState(false);

  useEffect(() => {
    if (isShow) {
      setAttach(true);
      setShow(true);
    } else {
      setShow(false);
      setTimeout(() => setAttach(false), TransitionTime - 100);
    }
  }, [isShow]);

  return <StyledAnimateLoading isActive={show} isDisplay={attach} backgroundColor={backgroundColor} />;
};

export const TransitionFullPageLoading = ({
  relative = false,
  backgroundColor = 'transparent',
  isShow,
}: {
  relative?: boolean;
  backgroundColor?: string;
  isShow: boolean;
}) => {
  const { height: viewportHeight } = useViewPort();

  const [show, setShow] = useState(isShow);
  const [attach, setAttach] = useState(false);

  useEffect(() => {
    if (isShow) {
      setAttach(true);
      setShow(true);
    } else {
      setShow(false);
      setTimeout(() => setAttach(false), TransitionTime - 100);
    }
  }, [isShow]);

  return (
    <StyledAnimateFullPageLoading
      isActive={show}
      isDisplay={attach}
      height={viewportHeight}
      relative={relative}
      backgroundColor={backgroundColor}
    />
  );
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const StyledLoadingImage = styled.img`
  width: 32px;
  height: 32px;
`;

const StyledFullPageLoading = styled(Loading)(
  ({ height, relative, backgroundColor }: { height: number; relative: boolean; backgroundColor: string }) => css`
    ${!relative &&
    `
      position: absolute;
      top: 0;
    `}
    ${ViewPortHeight(height)};

    z-index: ${ZIndex.FullPageLoading};
    background-color: ${backgroundColor};
  `,
);

const StyledAnimateFullPageLoading = styled(Loading)(
  ({
    height,
    relative,
    backgroundColor,
    isActive,
    isDisplay,
  }: {
    height: number;
    relative: boolean;
    backgroundColor: string;
    isActive: boolean;
    isDisplay: boolean;
  }) => css`
    ${!relative &&
    `
      position: absolute;
      top: 0;
    `}
    ${ViewPortHeight(height)};

    display: ${isDisplay ? 'flex' : 'none'};
    z-index: ${ZIndex.FullPageLoading};
    background-color: ${backgroundColor};
    animation: ${isActive ? fadeIn : fadeOut} ${TransitionTime}ms ease-out alternate;
  `,
);

const StyledAnimateLoading = styled(Loading)(
  ({ backgroundColor, isActive, isDisplay }: { backgroundColor: string; isActive: boolean; isDisplay: boolean }) => css`
    position: absolute;
    top: 100px;
    display: ${isDisplay ? 'flex' : 'none'};
    z-index: ${ZIndex.FullPageLoading};
    background-color: ${backgroundColor};
    animation: ${isActive ? fadeIn : fadeOut} ${TransitionTime}ms ease-out alternate;
  `,
);
